import { FormEvent, useState } from 'react';
import { LockKeyhole, ShieldCheck } from 'lucide-react';
import { USERS, type Username } from '../utils/constants';
import { toFriendlyError } from '../utils/errors';

type LoginFormProps = {
  authError?: string;
  onLogin: (username: Username, password: string) => Promise<void>;
};

export function LoginForm({ authError = '', onLogin }: LoginFormProps) {
  const [username, setUsername] = useState<Username>('user1');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(authError);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onLogin(username, password);
    } catch (caught) {
      setError(toFriendlyError(caught));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="login-form" onSubmit={handleSubmit}>
      <div className="field-group">
        <label htmlFor="username">Trusted user</label>
        <div className="segmented-control" role="radiogroup" aria-label="Trusted user">
          {USERS.map((user) => (
            <button
              className={username === user ? 'selected' : ''}
              type="button"
              role="radio"
              aria-checked={username === user}
              key={user}
              onClick={() => setUsername(user)}
            >
              {user}
            </button>
          ))}
        </div>
      </div>

      <div className="field-group">
        <label htmlFor="password">Private password</label>
        <div className="input-shell">
          <LockKeyhole size={18} aria-hidden="true" />
          <input
            id="password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter shared password"
            required
          />
        </div>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <button className="notify-button" type="submit" disabled={isSubmitting}>
        <ShieldCheck size={18} aria-hidden="true" />
        {isSubmitting ? 'Checking...' : 'Unlock SOS Signal'}
      </button>
    </form>
  );
}
